/**
 * Background video for the product spotlight section.
 *
 * Two jobs a plain <video> can't do on its own:
 *
 *   1. Serve a different cut per breakpoint without downloading both. `<source media>` is
 *      not honoured for video — browsers only ever implemented it for <picture> — so the
 *      URLs ship as data-psp-src and only the ones matching the current view are promoted
 *      to a real src. A source with no src is inert, so nothing is fetched until then:
 *      a phone never pulls the desktop file, and a desktop never pulls the mobile one.
 *   2. Drive the play/pause control (Figma 3241:3319).
 *
 * Autoplay is muted and inline, which is what Chrome and Safari require before they will
 * allow it at all, and it is unconditional — prefers-reduced-motion is deliberately not
 * consulted. The control is the visitor's way out.
 *
 * Parts, by data attribute:
 *   source[data-psp-src][data-psp-view] — a source for 'desktop', 'mobile' or 'both'
 *   [data-psp-playpause]                — optional toggle button
 *
 * Reflects `data-playing` onto itself so CSS can swap the button's icon, and keeps the
 * button's aria-label in step with what pressing it will do.
 *
 * Used by sections/product-spotlight.liquid.
 */

const MOBILE_VIEW = '(max-width: 749px)';

class ProductSpotlightVideo extends HTMLElement {
  /** Media events after which the control may need to change. */
  static #STATE_EVENTS = ['play', 'playing', 'pause', 'ended', 'error'];

  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {MediaQueryList | null} */
  #mobile = null;

  /**
   * Whether the visitor has pressed pause. Swapping cuts at a breakpoint re-loads the
   * element, and this is what stops that re-load from quietly restarting a video they
   * stopped on purpose.
   * @type {boolean}
   */
  #stopped = false;

  #onViewChange = () => this.#applyView();

  #onPlayState = () => this.#syncButton();

  connectedCallback() {
    this.#video = this.querySelector('video');
    this.#button = this.querySelector('[data-psp-playpause]');

    if (!this.#video) return;

    this.#mobile = matchMedia(MOBILE_VIEW);

    // Synced once up front so the resting state is correct before playback is attempted,
    // then driven by events. `play` is in that list deliberately: it fires the moment
    // play() is called, which is what puts the pause icon up immediately on load rather
    // than waiting for the first decoded frame. `playing` keeps it honest afterwards, and
    // `pause`/`error` — plus the rejection handler in #play — are what put the play icon
    // back if the browser refuses the autoplay outright.
    this.#syncButton();

    this.#mobile.addEventListener('change', this.#onViewChange);
    for (const event of ProductSpotlightVideo.#STATE_EVENTS) {
      this.#video.addEventListener(event, this.#onPlayState);
    }
    this.#button?.addEventListener('click', () => this.#toggle());

    this.#applyView();
  }

  disconnectedCallback() {
    this.#mobile?.removeEventListener('change', this.#onViewChange);

    for (const event of ProductSpotlightVideo.#STATE_EVENTS) {
      this.#video?.removeEventListener(event, this.#onPlayState);
    }
  }

  /**
   * Points the <video> at the sources for the current breakpoint. Called again whenever the
   * breakpoint changes — on a rotate or a resized desktop window — and re-loads only if the
   * set of live sources actually differs, so a resize that doesn't cross 750px is free.
   */
  #applyView() {
    const video = this.#video;
    if (!video) return;

    const view = this.#mobile?.matches ? 'mobile' : 'desktop';
    let changed = false;

    for (const source of this.querySelectorAll('source[data-psp-src]')) {
      const wanted = source.dataset.pspView === view || source.dataset.pspView === 'both';
      const live = source.hasAttribute('src');

      if (wanted && !live) {
        source.setAttribute('src', source.dataset.pspSrc ?? '');
        changed = true;
      } else if (!wanted && live) {
        source.removeAttribute('src');
        changed = true;
      }
    }

    if (!changed) return;

    // load() drops whatever was playing, so decide first whether it should come back: it
    // should unless the visitor stopped it themselves.
    const resume = !video.paused || !this.#stopped;

    video.load();
    if (resume) this.#play();
  }

  #play() {
    this.#stopped = false;
    // A rejected play() is routine — a background tab, or a browser declining the autoplay
    // despite the muted+inline attributes. The control is the recovery path, so all this
    // needs to do is make sure the icon reflects the refusal.
    this.#video?.play().catch(() => this.#syncButton());
  }

  #toggle() {
    if (!this.#video) return;

    if (this.#video.paused) {
      this.#play();
    } else {
      this.#stopped = true;
      this.#video.pause();
    }
  }

  #syncButton() {
    const playing = !!this.#video && !this.#video.paused && !this.#video.error;

    this.toggleAttribute('data-playing', playing);

    if (!this.#button) return;

    const { labelPlay, labelPause } = this.#button.dataset;
    this.#button.setAttribute('aria-label', (playing ? labelPause : labelPlay) ?? '');
  }
}

if (!customElements.get('product-spotlight-video')) {
  customElements.define('product-spotlight-video', ProductSpotlightVideo);
}
